import type { ChangeRequest, LocalizedText, ModerationEntity } from '../model/schema';
import { resolveEntityName } from './names';

/**
 * Readers for the loose live-entity bag (each queue returns its own
 * localized message). They reach the fields tables actually render —
 * photo, category, price, lifecycle — without pretending one shape fits all.
 */

export type EntityBag = Record<string, unknown>;

export function entityBag(entity: ModerationEntity): EntityBag {
  return entity;
}

export function isLocalizedText(value: unknown): value is LocalizedText {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const bag = value as Record<string, unknown>;
  return (
    typeof bag.ru === 'string' || typeof bag.uz === 'string' || typeof bag.en === 'string'
  );
}

/** Localized (`{ru, uz, en}`) or plain-string field → display string. */
export function entityText(
  entity: ModerationEntity,
  key: string,
  fallback = '—',
): string {
  const value: unknown = entityBag(entity)[key];
  if (isLocalizedText(value)) return resolveEntityName(value, fallback);
  if (typeof value === 'string') return value.trim() !== '' ? value.trim() : fallback;
  return fallback;
}

/** Plain-string field (ids, phones, category names, enum codes). */
export function entityString(
  entity: ModerationEntity,
  key: string,
  fallback = '',
): string {
  const value: unknown = entityBag(entity)[key];
  if (typeof value === 'string') return value.trim() !== '' ? value.trim() : fallback;
  return fallback;
}

/** Numeric field (prices, counts) — non-finite values read as missing. */
export function entityNumber(entity: ModerationEntity, key: string): number | undefined {
  const value: unknown = entityBag(entity)[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const coerced = Number(value);
  return typeof value === 'string' && Number.isFinite(coerced) ? coerced : undefined;
}

/** Boolean field. */
export function entityBoolean(
  entity: ModerationEntity,
  key: string,
): boolean | undefined {
  const value: unknown = entityBag(entity)[key];
  return typeof value === 'boolean' ? value : undefined;
}

/** Raw localized bag behind a field key (name, description, address line). */
export function entityLocalized(
  entity: ModerationEntity,
  key: string,
): LocalizedText | undefined {
  const value: unknown = entityBag(entity)[key];
  return isLocalizedText(value) ? value : undefined;
}

/**
 * The `name` field as localized text. Most entities carry `{ru, uz, en}`;
 * a few send a plain string — those read as the `ru` line.
 */
export function entityName(entity: ModerationEntity): LocalizedText | undefined {
  const value: unknown = entityBag(entity).name;
  if (isLocalizedText(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    return { ru: value, uz: null, en: null };
  }
  return undefined;
}

/** Length of an array field (`product_ids`, `images`) — non-arrays read as 0. */
export function entityArrayLength(entity: ModerationEntity, key: string): number {
  const value: unknown = entityBag(entity)[key];
  return Array.isArray(value) ? value.length : 0;
}

/* ── Request payload / baseline ──────────────────────────────────────── */
/** The request payload as a plain bag (Struct by design — see schema.ts). */
export function payloadBag(request: Pick<ChangeRequest, 'payload'>): EntityBag {
  return structBag(request.payload) ?? {};
}

/** A Struct field (`payload` / `baseline` / `original_payload`) as a bag. */
export function structBag(value: unknown): EntityBag | null {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return value as EntityBag;
  }
  return null;
}

/** Raw payload / baseline bags for the modal's "show everything" dumps. */
export function requestPayloadOf(
  request: Pick<ChangeRequest, 'payload'>,
): Record<string, unknown> {
  return payloadBag(request);
}

export function requestBaselineOf(
  request: Pick<ChangeRequest, 'baseline'>,
): Record<string, unknown> | null {
  return structBag(request.baseline);
}

/** The merchant's own payload on an amended request, else `null`. */
export function requestOriginalPayloadOf(
  request: Pick<ChangeRequest, 'original_payload'>,
): Record<string, unknown> | null {
  return structBag(request.original_payload);
}

/**
 * Proposed (`payload`) / previous (`baseline`) data as entity bags, so the
 * shared `entity*` readers serve the queue table and the compare modal
 * without a second accessor family. Absent for creates (no baseline yet).
 */
export function payloadEntityOf(
  request: Pick<ChangeRequest, 'payload'>,
): ModerationEntity {
  return payloadBag(request);
}

export function baselineEntityOf(
  request: Pick<ChangeRequest, 'baseline'>,
): ModerationEntity | null {
  return requestBaselineOf(request);
}
